var _excluded = ["size", "shadowColor", "bg", "style"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useCallback, useEffect, useRef } from 'react';
import { createPopper } from '@popperjs/core';
import * as customModifiers from './modifiers';
import { getPopperPlacement } from './popper.placement';
import { cssVars, getEventListenerOptions } from './utils';
import { mergeRefs } from '../..';
export function usePopper() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var {
    enabled = true,
    modifiers,
    placement: placementProp = 'bottom',
    strategy = 'absolute',
    arrowPadding = 8,
    eventListeners = true,
    offset,
    gutter = 8,
    flip = true,
    boundary = 'clippingParents',
    preventOverflow = true,
    matchWidth,
    direction = 'ltr'
  } = props;
  var reference = useRef(null);
  var popper = useRef(null);
  var instance = useRef(null);
  var placement = getPopperPlacement(placementProp, direction);
  var cleanup = useRef(() => {});
  var setupPopper = useCallback(() => {
    var _cleanup$current;
    if (!enabled || !reference.current || !popper.current) return;

    // If popper instance exists, destroy it so we can create a new one
    (_cleanup$current = cleanup.current) === null || _cleanup$current === void 0 ? void 0 : _cleanup$current.call(cleanup);
    instance.current = createPopper(reference.current, popper.current, {
      placement,
      modifiers: [customModifiers.innerArrow, customModifiers.positionArrow, customModifiers.transformOrigin, _objectSpread(_objectSpread({}, customModifiers.matchWidth), {}, {
        enabled: !!matchWidth
      }), _objectSpread({
        name: 'eventListeners'
      }, getEventListenerOptions(eventListeners)), {
        name: 'arrow',
        options: {
          padding: arrowPadding
        }
      }, {
        name: 'offset',
        options: {
          offset: offset !== null && offset !== void 0 ? offset : [0, gutter]
        }
      }, {
        name: 'flip',
        enabled: !!flip,
        options: {
          padding: 8
        }
      }, {
        name: 'preventOverflow',
        enabled: !!preventOverflow,
        options: {
          boundary
        }
      },
      // allow users override internal modifiers
      ...(modifiers !== null && modifiers !== void 0 ? modifiers : [])],
      strategy
    });

    // force update one-time to fix any positioning issues
    instance.current.forceUpdate();
    cleanup.current = instance.current.destroy;
  }, [placement, enabled, modifiers, matchWidth, eventListeners, arrowPadding, offset, gutter, flip, preventOverflow, boundary, strategy]);
  useEffect(() => {
    return () => {
      /**
       * Fast refresh might call this function and tear down the popper
       * even if the reference still exists. This checks against that
       */
      if (!reference.current && !popper.current) {
        var _instance$current;
        (_instance$current = instance.current) === null || _instance$current === void 0 ? void 0 : _instance$current.destroy();
        instance.current = null;
      }
    };
  }, []);
  var referenceRef = useCallback(node => {
    reference.current = node;
    setupPopper();
  }, [setupPopper]);
  var getReferenceProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      ref: mergeRefs(referenceRef, ref)
    });
  }, [referenceRef]);
  var popperRef = useCallback(node => {
    popper.current = node;
    setupPopper();
  }, [setupPopper]);
  var getPopperProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      ref: mergeRefs(popperRef, ref),
      style: _objectSpread(_objectSpread({}, props.style), {}, {
        position: strategy,
        minWidth: 'max-content',
        inset: '0 auto auto 0'
      })
    });
  }, [strategy, popperRef]);
  var getArrowProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var {
        size,
        shadowColor,
        bg,
        style
      } = props,
      rest = _objectWithoutProperties(props, _excluded);
    return _objectSpread(_objectSpread({}, rest), {}, {
      ref,
      'data-popper-arrow': '',
      style: getArrowStyle(props)
    });
  }, []);
  var getArrowInnerProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      ref,
      'data-popper-arrow-inner': ''
    });
  }, []);
  return {
    update() {
      var _instance$current2;
      (_instance$current2 = instance.current) === null || _instance$current2 === void 0 ? void 0 : _instance$current2.update();
    },
    forceUpdate() {
      var _instance$current3;
      (_instance$current3 = instance.current) === null || _instance$current3 === void 0 ? void 0 : _instance$current3.forceUpdate();
    },
    transformOrigin: cssVars.transformOrigin.varRef,
    referenceRef,
    popperRef,
    getPopperProps,
    getArrowProps,
    getArrowInnerProps,
    getReferenceProps
  };
}
function getArrowStyle(props) {
  var {
    size,
    shadowColor,
    bg,
    style
  } = props;
  var computedStyle = _objectSpread(_objectSpread({}, style), {}, {
    position: 'absolute'
  });
  if (size) {
    computedStyle['--popper-arrow-size'] = size;
  }
  if (shadowColor) {
    computedStyle['--popper-arrow-shadow-color'] = shadowColor;
  }
  if (bg) {
    computedStyle['--popper-arrow-bg'] = bg;
  }
  return computedStyle;
}